import api from '../../api';

export default {
  name: 'accountProfile',
  component() {
    return {
      loading: false,
      initialLoading: false,
      error: null,
      success: false,
      isEditMode: false,
      customerData: {},
      hasUpdates: false,
      originalFormData: {},
      formData: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        acceptsMarketing: false,
        password: '',
        confirmPassword: ''
      },

      async init() {
        await this.fetchCustomerData();
        this.initializeFormData();
      },

      async fetchCustomerData() {
        this.initialLoading = true;
        this.error = null;

        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          this.error = 'No access token found';
          this.initialLoading = false;
          return;
        }

        try {
          const query = `
            query {
              customer(customerAccessToken: "${accessToken}") {
                id
                firstName
                lastName
                email
                phone
                acceptsMarketing
              }
            }
          `;

          const response = await fetch('/api/2025-07/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
            },
            body: JSON.stringify({
              query: query
            })
          });

          if (!response.ok) {
            throw new Error('Failed to fetch customer data');
          }

          const { data, errors } = await response.json();

          if (errors) {
            throw new Error(errors[0].message);
          }

          this.customerData = data.customer || {};
        } catch (error) {
          console.error('Error fetching customer:', error);
          this.error = error.message;
        } finally {
          this.initialLoading = false;
        }
      },

      initializeFormData() {
        if (this.customerData) {
          this.formData = {
            firstName: this.customerData.firstName || '',
            lastName: this.customerData.lastName || '',
            email: this.customerData.email || '',
            phone: this.customerData.phone || '',
            acceptsMarketing: this.customerData.acceptsMarketing || false,
            password: '',
            confirmPassword: ''
          };

          // Store original form data for comparison
          this.originalFormData = {
            firstName: this.customerData.firstName || '',
            lastName: this.customerData.lastName || '',
            email: this.customerData.email || '',
            phone: this.customerData.phone || '',
            acceptsMarketing: this.customerData.acceptsMarketing || false,
            password: '',
            confirmPassword: ''
          };
        }
      },

      get hasChanges() {
        // Check if any form field has changed from the original values
        return (
          this.formData.firstName !== this.originalFormData.firstName ||
          this.formData.lastName !== this.originalFormData.lastName ||
          this.formData.email !== this.originalFormData.email ||
          this.formData.phone !== this.originalFormData.phone ||
          this.formData.acceptsMarketing !== this.originalFormData.acceptsMarketing ||
          this.formData.password !== '' ||
          this.formData.confirmPassword !== ''
        );
      },

      toggleEditMode() {
        this.isEditMode = !this.isEditMode;
        this.error = null;
        this.success = false;

        // Reset form data when entering edit mode
        if (this.isEditMode) {
          this.initializeFormData();
        }
      },

      async updateProfile() {
        this.loading = true;
        this.error = null;
        this.success = false;

        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          this.error = 'No access token found';
          this.loading = false;
          return;
        }

        try {
          // Check if password update is needed
          const hasPasswordUpdate = this.formData.password || this.formData.confirmPassword;

          if (hasPasswordUpdate) {
            // Validate password fields
            if (!this.formData.password) {
              throw new Error('Password is required');
            }

            if (!this.formData.confirmPassword) {
              throw new Error('Password confirmation is required');
            }

            if (this.formData.password !== this.formData.confirmPassword) {
              throw new Error('Password and password confirmation must match');
            }
          }

          // Always update profile first
          await this.updateCustomerProfile(accessToken);

          // If password update is needed, update password separately
          if (hasPasswordUpdate) {
            await this.updateCustomerPassword(accessToken);
          }

          this.success = true;
          this.hasUpdates = true;

          // Update the original form data to reflect the new saved state
          this.originalFormData = {
            firstName: this.formData.firstName,
            lastName: this.formData.lastName,
            email: this.formData.email,
            phone: this.formData.phone,
            acceptsMarketing: this.formData.acceptsMarketing,
            password: '',
            confirmPassword: ''
          };

          // Clear password fields after successful update
          this.formData.password = '';
          this.formData.confirmPassword = '';

          // Exit edit mode after successful update
          setTimeout(() => {
            this.isEditMode = false;
            this.success = false;
          }, 2000);
        } catch (error) {
          console.error('Error updating profile:', error);
          this.error = error.message;
        } finally {
          this.loading = false;
        }
      },

      async updateCustomerProfile(accessToken) {
        const mutation = `
          mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
            customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
              customer {
                id
                firstName
                lastName
                email
                phone
                acceptsMarketing
              }
              customerUserErrors {
                code
                field
                message
              }
            }
          }
        `;

        const variables = {
          customerAccessToken: accessToken,
          customer: {
            firstName: this.formData.firstName,
            lastName: this.formData.lastName,
            email: this.formData.email,
            phone: this.formData.phone,
            acceptsMarketing: this.formData.acceptsMarketing
          }
        };

        const response = await fetch('/api/2025-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
          },
          body: JSON.stringify({
            query: mutation,
            variables: variables
          })
        });

        const result = await response.json();

        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        const { customerUpdate } = result.data;
        const { customer, customerUserErrors } = customerUpdate;

        if (customerUserErrors && customerUserErrors.length > 0) {
          throw new Error(customerUserErrors[0].message);
        }

        if (customer) {
          this.customerData = customer;
        }
      },

      async updateCustomerPassword(accessToken) {
        const mutation = `
          mutation customerUpdate($customer: CustomerUpdateInput!, $customerAccessToken: String!) {
            customerUpdate(customer: $customer, customerAccessToken: $customerAccessToken) {
              customer {
                id
              }
              customerAccessToken {
                accessToken
                expiresAt
              }
              customerUserErrors {
                code
                field
                message
              }
              userErrors {
                field
                message
              }
            }
          }
        `;

        const variables = {
          customer: {
            password: this.formData.password
          },
          customerAccessToken: accessToken
        };

        const response = await fetch('/api/2024-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
          },
          body: JSON.stringify({
            query: mutation,
            variables: variables
          })
        });

        if (!response.ok) {
          throw new Error('Failed to update password. Please try another time.');
        }

        const { data, errors } = await response.json();

        if (errors) {
          throw new Error(errors[0].message);
        }

        const { userErrors } = data.customerUpdate;
        if (userErrors && userErrors.length > 0) {
          let errorMessage = '';
          userErrors.forEach((error) => {
            errorMessage += `${error.message}\n`;
          });
          throw new Error(errorMessage);
        }

        const { customerUserErrors } = data.customerUpdate;
        if (customerUserErrors && customerUserErrors.length > 0) {
          let errorMessage = '';
          customerUserErrors.forEach((error) => {
            errorMessage += `${error.message}\n`;
          });
          throw new Error(errorMessage);
        }

        // Update access token if password was changed successfully
        const newAccessToken = data.customerUpdate.customerAccessToken?.accessToken;
        if (newAccessToken) {
          localStorage.setItem('accessToken', newAccessToken);
        }
      },

      getDisplayName() {
        const firstName = this.customerData.firstName || '';
        const lastName = this.customerData.lastName || '';
        
        if (firstName && lastName) {
          return `${firstName} ${lastName}`;
        }
        if (firstName) {
          return firstName;
        }
        if (lastName) {
          return lastName;
        }
        return '-';
      },

    };
  }
};
